import React, { createContext, useContext, useMemo, type ReactNode } from 'react';
import { fg } from '@atlassian/jira-feature-gating';
import { componentWithFG } from '@atlassian/jira-feature-gate-component';
import type {
	IssueNavigatorFeaturesContext,
	IssueNavigatorFeatureProviderProps,
	IssueNavigatorFeatureGuardProps,
} from './types.tsx';

const defaultFeatureContext: IssueNavigatorFeaturesContext = {
	isViewSwitcherEnabled: true,
	isOnboardingEnabled: true,
	isExportMenuEnabled: true,
	isRowContextMenuEnabled: true,
	isSaveFilterButtonEnabled: true,
	isBulkChangeWorkItemsEnabled: true,
	isImportWorkItemsFromCSVEnabled: true,
	isViewAsChartEnabled: true,
	isConditionalFormattingEnabled: false, // TODO: jira_list_conditional_formatting
	isProjectQuickstartOnboardingEnabled: false,
};

const IssueNavigatorFeatureContext =
	createContext<IssueNavigatorFeaturesContext>(defaultFeatureContext);

const useIssueNavigatorFeatures = () => useContext(IssueNavigatorFeatureContext);

const useIssueNavigatorFeature = (featureKey: keyof IssueNavigatorFeaturesContext) =>
	useIssueNavigatorFeatures()[featureKey];

const IssueNavigatorFeatureOldProvider = ({
	children,
	...props
}: IssueNavigatorFeatureProviderProps) => {
	const value = useMemo(() => ({ ...defaultFeatureContext, ...props }), [props]);

	return (
		<IssueNavigatorFeatureContext.Provider value={value}>
			{children}
		</IssueNavigatorFeatureContext.Provider>
	);
};

const IssueNavigatorFeatureNewProvider = ({
	children,
	isBulkChangeWorkItemsEnabled,
	isConditionalFormattingEnabled,
	isExportMenuEnabled,
	isImportWorkItemsFromCSVEnabled,
	isOnboardingEnabled,
	isProjectQuickstartOnboardingEnabled,
	isRowContextMenuEnabled,
	isSaveFilterButtonEnabled,
	isViewAsChartEnabled,
	isViewSwitcherEnabled,
}: IssueNavigatorFeatureProviderProps) => {
	const value = useMemo(
		() => ({
			isBulkChangeWorkItemsEnabled:
				isBulkChangeWorkItemsEnabled ?? defaultFeatureContext.isBulkChangeWorkItemsEnabled,
			isConditionalFormattingEnabled:
				isConditionalFormattingEnabled ?? defaultFeatureContext.isConditionalFormattingEnabled,
			isExportMenuEnabled: isExportMenuEnabled ?? defaultFeatureContext.isExportMenuEnabled,
			isImportWorkItemsFromCSVEnabled:
				isImportWorkItemsFromCSVEnabled ?? defaultFeatureContext.isImportWorkItemsFromCSVEnabled,
			isOnboardingEnabled: isOnboardingEnabled ?? defaultFeatureContext.isOnboardingEnabled,
			isProjectQuickstartOnboardingEnabled:
				isProjectQuickstartOnboardingEnabled ??
				defaultFeatureContext.isProjectQuickstartOnboardingEnabled,
			isRowContextMenuEnabled:
				isRowContextMenuEnabled ?? defaultFeatureContext.isRowContextMenuEnabled,
			isSaveFilterButtonEnabled:
				isSaveFilterButtonEnabled ?? defaultFeatureContext.isSaveFilterButtonEnabled,
			isViewAsChartEnabled: isViewAsChartEnabled ?? defaultFeatureContext.isViewAsChartEnabled,
			isViewSwitcherEnabled: isViewSwitcherEnabled ?? defaultFeatureContext.isViewSwitcherEnabled,
		}),
		[
			isBulkChangeWorkItemsEnabled,
			isConditionalFormattingEnabled,
			isExportMenuEnabled,
			isImportWorkItemsFromCSVEnabled,
			isOnboardingEnabled,
			isProjectQuickstartOnboardingEnabled,
			isRowContextMenuEnabled,
			isSaveFilterButtonEnabled,
			isViewAsChartEnabled,
			isViewSwitcherEnabled,
		],
	);

	return (
		<IssueNavigatorFeatureContext.Provider value={value}>
			{children}
		</IssueNavigatorFeatureContext.Provider>
	);
};

const IssueNavigatorFeatureProvider = componentWithFG(
	'jira_list_conditional_formatting_feature_context',
	IssueNavigatorFeatureNewProvider,
	IssueNavigatorFeatureOldProvider,
);

const IssueNavigatorFeatureGuard = ({ featureKey, on, off }: IssueNavigatorFeatureGuardProps) => {
	const feature = useIssueNavigatorFeature(featureKey);
	return feature ? on : off;
};

type IssueNavigatorFeatureGuardGatedProps = IssueNavigatorFeatureGuardProps & {
	fallback: ReactNode;
};

const IssueNavigatorFeatureGuardGated = ({
	fallback,
	...props
}: IssueNavigatorFeatureGuardGatedProps) => {
	return fg('jira_nin_smart_link') ? <IssueNavigatorFeatureGuard {...props} /> : fallback;
};

export {
	IssueNavigatorFeatureProvider,
	useIssueNavigatorFeatures,
	useIssueNavigatorFeature,
	IssueNavigatorFeatureGuardGated as IssueNavigatorFeatureGuard,
};
